import classNames from 'classnames';
import BaseInput from 'components/atoms/Inputs/BaseInput.component';
import { Loader } from 'components/atoms/Loader';
import { useHighlight } from 'hooks/useHighlight';
import type { PropertyPath } from 'lodash';
import _get from 'lodash/get';
import type { FC } from 'react';
import { forwardRef, useState } from 'react';
import { getTracingEvents } from 'utils/amplitude';
import { captureLeftRightArrowEvents } from 'utils/captureLeftRightArrowEvents';
import { EyeIconOutline, EyeOffIconOutline } from 'utils/icons';
import { KeyIconSolidMiniIcon } from 'utils/icons/figmaIcons';
import type FreeTextInputProps from './FreeTextInput.types';

const LoadingIndicator = () => <Loader />;

const FreeTextInput: FC<FreeTextInputProps> = forwardRef(
  (
    {
      errors,
      className: _className,
      wrapperClassName,
      type = 'text',
      Icon,
      loading = false,
      // InfoComponent,
      connectField = {},
      tracing,
      onFocus,
      highlightID,
      showErrorIcon = true,
      renderExtraRight,
      maskProps,
      onChange,
      innerClassName,
      ...props
    },
    ref,
  ) => {
    const IconComponent = Icon || (type === 'password' && KeyIconSolidMiniIcon);

    const { message: error }: { message?: string } = _get(
      errors,
      (connectField.name || props.name) as PropertyPath,
      {},
    );
    const _error = props.error || error;

    const tracingEvents = getTracingEvents(tracing, {
      onFocus,
      onChange,
    });

    const highlightAPI = useHighlight();

    const className = classNames(
      _className,
      highlightAPI.shouldHighlight(highlightID) && highlightAPI.className,
    );

    const [innerType, setInnerType] = useState(type);

    return (
      <BaseInput
        {...tracingEvents}
        {...props}
        {...connectField}
        {...maskProps}
        ref={connectField.ref || ref}
        type={innerType}
        onKeyDown={(e) => {
          props.onKeyDown?.(e);
          captureLeftRightArrowEvents(e);
        }}
        error={_error}
        className={className}
        data-highlight-id={highlightID}
        containerClassName={wrapperClassName}
        innerClassName={innerClassName}
        LeftIcon={IconComponent}
        RightIcon={loading ? LoadingIndicator : undefined}
        renderExtraRight={(className) => {
          if (renderExtraRight) return renderExtraRight(className);
          if (type !== 'password') return null;
          return (
            <button
              type="button"
              className={className}
              onClick={() =>
                setInnerType((state) =>
                  state === 'password' ? 'text' : 'password',
                )
              }
            >
              {innerType === 'password' ? (
                <EyeIconOutline className="text-primary-500 w-5 h-5" />
              ) : (
                <EyeOffIconOutline className="text-primary-500 w-5 h-5" />
              )}
            </button>
          );
        }}
      />
    );
  },
);
FreeTextInput.displayName = 'FreeTextInput';
export default FreeTextInput;
